import {
  Component,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Product } from 'src/app/models/product.model';
import { ProductsService } from 'src/app/services/product.service';
import { AuthService } from 'src/app/services/auth.service';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export interface MenuCategory {
  name: string;
  icon: string;
  products: Product[];
}

@Component({
  selector: 'app-menu-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './menu-card.component.html',
  styleUrls: ['./menu-card.component.css'],
})
export class MenuCardComponent implements OnInit {
  private productsService = inject(ProductsService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);

  /** 'fixed' or 'itinerant' — drives the visual style */
  @Input() merchantType: 'fixed' | 'itinerant' = 'fixed';

  /** Optional business name override */
  @Input() businessName = '';

  /** Optional: provide products directly (skips API fetch) */
  @Input() products: Product[] | null = null;

  /** Emits when the modal should close */
  @Output() closed = new EventEmitter<void>();

  @ViewChild('menuContent') menuContent!: ElementRef<HTMLDivElement>;

  categories: MenuCategory[] = [];
  loading = true;
  exporting = false;

  /** Total product count */
  get totalProducts(): number {
    return this.categories.reduce((sum, c) => sum + c.products.length, 0);
  }

  ngOnInit(): void {
    if (!this.businessName) {
      const user = this.authService.currentUser();
      this.businessName = user?.businessName || 'Mon Commerce';
    }
    if (this.products) {
      const active = this.products.filter((p) => p.isActive !== false);
      this.categories = this.groupByCategory(active);
      this.loading = false;
    } else {
      this.loadProducts();
    }
  }

  private loadProducts(): void {
    this.productsService.getMyProducts().subscribe({
      next: (products) => {
        const active = products.filter((p) => p.isActive !== false);
        this.categories = this.groupByCategory(active);
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private groupByCategory(products: Product[]): MenuCategory[] {
    const map = new Map<string, Product[]>();
    for (const p of products) {
      const cat = p.category || 'Autres';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(p);
    }
    // Sort categories alphabetically, products by name
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, items]) => ({
        name,
        icon: items[0]?.productIcon || '📦',
        products: items.sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }

  /** Format price for display */
  formatPrice(price: number | undefined): string {
    if (!price && price !== 0) return '';
    return price.toFixed(2).replace('.', ',') + ' €';
  }

  /** Export as PNG */
  async exportPNG(): Promise<void> {
    if (!this.menuContent) return;
    this.exporting = true;
    try {
      const canvas = await html2canvas(this.menuContent.nativeElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      });
      const link = document.createElement('a');
      link.download = `menu-${this.businessName.replace(/\s+/g, '_')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      this.exporting = false;
    }
  }

  /** Export as PDF */
  async exportPDF(): Promise<void> {
    if (!this.menuContent) return;
    this.exporting = true;
    try {
      const canvas = await html2canvas(this.menuContent.nativeElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      });
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 190; // A4 width minus margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const doc = new jsPDF('p', 'mm', 'a4');
      let yOffset = 10;
      const pageHeight = 277; // A4 usable height

      // Handle multi-page if content is tall
      if (imgHeight <= pageHeight) {
        doc.addImage(imgData, 'PNG', 10, yOffset, imgWidth, imgHeight);
      } else {
        // Split across pages
        let remainingHeight = imgHeight;
        while (remainingHeight > 0) {
          doc.addImage(imgData, 'PNG', 10, yOffset, imgWidth, imgHeight);
          remainingHeight -= pageHeight;
          if (remainingHeight > 0) {
            doc.addPage();
            yOffset = -(imgHeight - remainingHeight);
          }
        }
      }

      doc.save(`menu-${this.businessName.replace(/\s+/g, '_')}.pdf`);
    } finally {
      this.exporting = false;
    }
  }

  close(): void {
    this.closed.emit();
  }
}
